import type Phaser from "phaser";
import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import { UI_BANDS } from "../objects/depthLayers.js";
import { PALETTE_NUM } from "../themes/palette.js";

const PAD = 5;
const DURATION_MS = 1400;

export class GuideQuestHighlighter {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private tween: Phaser.Tweens.Tween | null = null;
  private clearTimer: Phaser.Time.TimerEvent | null = null;

  constructor(private readonly scene: Phaser.Scene) {
    this.graphics = scene.add
      .graphics()
      .setDepth(UI_BANDS.modal.base - 20)
      .setVisible(false)
      .setActive(false);
  }

  highlight(rect: Rect): void {
    this.highlightMany([rect]);
  }

  highlightMany(rects: readonly Rect[]): void {
    this.cancelTransient();
    if (rects.length === 0) {
      this.clear();
      return;
    }

    this.graphics.clear();
    for (const rect of rects) {
      const r = padRect(rect, PAD);
      this.graphics
        .fillStyle(PALETTE_NUM.accentAmber, 0.12)
      .fillRect(r.x, r.y, r.width, r.height)
      .lineStyle(3, PALETTE_NUM.accentAmber, 0.95)
      .strokeRect(r.x, r.y, r.width, r.height)
      .lineStyle(1, PALETTE_NUM.textPrimary, 0.75)
        .strokeRect(r.x + 3, r.y + 3, Math.max(1, r.width - 6), Math.max(1, r.height - 6));
    }
    this.graphics
      .setAlpha(1)
      .setVisible(true)
      .setActive(true);

    this.tween = this.scene.tweens.add({
      targets: this.graphics,
      alpha: 0.35,
      duration: 220,
      yoyo: true,
      repeat: 3,
      ease: "Sine.easeInOut",
    });
    this.clearTimer = this.scene.time.delayedCall(DURATION_MS, () => this.clear());
  }

  clear(): void {
    this.cancelTransient();
    this.graphics.clear().setVisible(false).setActive(false).setAlpha(1);
  }

  dispose(): void {
    this.cancelTransient();
    this.graphics.destroy();
  }

  private cancelTransient(): void {
    this.tween?.remove();
    this.tween = null;
    this.clearTimer?.remove(false);
    this.clearTimer = null;
  }
}

function padRect(rect: Rect, pad: number): Rect {
  return {
    x: rect.x - pad,
    y: rect.y - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
}
